import { Box, Button, Paper } from '@mui/material';
import {
  add,
  differenceInDays,
  getDay,
  getMonth,
  getYear,
  set,
} from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useResizeDetector } from 'react-resize-detector';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useRecoilValue } from 'recoil';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { getDayOfWeek, getMonthText, getTypeWord } from '../../common/helper';
import NoDataPage from '../commonComponent/NoDataPage';
import { SwTitleDownloadMore } from '../commonComponent/SwTitleDownloadMore';
import { SwRwwTab } from '../commonComponent/SwRwwTab';
import { RwwCumulativeGraphByGender } from './RwwCumulativeGraphByGender';
import {
  KEY_CLASS_MONTHLY_MEMBER_LIST,
  KEY_CLASS_MONTHLY_READ_RANK_LIST,
  KEY_CLASS_MONTHLY_WALK_RANK_LIST,
  KEY_CLASS_MONTHLY_WRITE_RANK_LIST,
  KEY_CUMULATIVE,
  KEY_READ_TEXT,
  KEY_WALK_TEXT,
  KEY_WRITE_TEXT,
} from '../../common/key';
import {
  downloadClassMonthlyDataList,
  downloadClassMonthlyMemberData,
  downloadClassYearAchievementData,
  getClassMonthlyMemberList,
  getClassMonthlyReadRankList,
  getClassMonthlyWalkRankList,
  getClassMonthlyWriteRankList,
  getClassYearReadAchievementData,
  getClassYearWalkAchievementData,
  getClassYearWriteAchievementData,
} from '../../common/api/ApiClassStat';
import { selectSchoolInfoState } from '../../common/atom';
import { RwwMemberGraphByGender } from './RwwMemberGraphByGender';

interface Props {
  startDate: number;
  from?: string;
  isPdf?: boolean;
}

export const RwwMemberStatistics: React.FC<Props> = ({
  startDate,
  from = 'dashboard',
  isPdf = false,
}) => {
  const schoolClassInfo = useRecoilValue(selectSchoolInfoState);
  const [reqRequest, setReqRequest] = useState<boolean>(false);
  const [requestData, setRequestData] = useState<any>();
  const [schoolSeq, setSchoolSeq] = useState<number>(0);
  const [schoolGrade, setSchoolGrade] = useState<string>('1');
  const [schoolClass, setSchoolClass] = useState<string>('1');
  const [rowCount, setRowCount] = useState<number>(1);
  const [rateData, setRateData] = useState<any>([]);
  const { width, ref } = useResizeDetector();
  const [loading, setLoading] = useState<any>(false);
  const [statType, setStatType] = useState<string>(KEY_READ_TEXT);

  const {
    data,
    isLoading,
    refetch: refetchClassMonthlyMemberData,
  } = useQuery(
    KEY_CLASS_MONTHLY_MEMBER_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          year: getYear(startDate * 1000),
        };
        return getClassMonthlyMemberList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const downloadClassMonthlyData = () => {
    const newData: any = {
      schoolSeq: schoolClassInfo.schoolSeq,
      schoolGrade: schoolClassInfo.schoolGrade,
      schoolClass: schoolClassInfo.schoolClass,
      year: getYear(startDate * 1000),
    };
    downloadClassMonthlyMemberData(newData);
  };

  useEffect(() => {
    if (reqRequest && schoolClassInfo.schoolSeq !== 0) {
      refetchClassMonthlyMemberData();
    }
  }, [schoolSeq, schoolGrade, schoolClass]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      setReqRequest(true);
      refetchClassMonthlyMemberData();
    }
  }, [schoolClassInfo]);

  useEffect(() => {
    if (reqRequest && schoolClassInfo.schoolSeq !== 0) {
      refetchClassMonthlyMemberData();
    }
  }, [reqRequest]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      setReqRequest(true);
      refetchClassMonthlyMemberData();
    }
  }, []);

  return (
    <>
      <Paper sx={{ backgroundColor: '#fff' }}>
        <Box className='flex_between'>
          <SwTitleDownloadMore title='우리 학교 사용자 수 데이터' />
          <Button
            sx={{ ml: '1rem' }}
            color='info'
            onClick={downloadClassMonthlyData}
          >
            데이터 다운로드
            <FileDownloadRoundedIcon color='success' />
          </Button>
        </Box>
        <RwwMemberGraphByGender
          stepsGoalRateAverage={[false, false]}
          startDate={startDate}
          totalAverage={requestData?.totalRate ?? 0}
          data={requestData?.statList}
          isPdf={false}
        />
      </Paper>
    </>
  );
};
